import dayjs from "dayjs";
import type { useTranslations } from "use-intl";
import { string, object, date, number, ref, ObjectSchema, mixed } from "yup";
import { CabinClass, TripType } from "@/app/lib/types/enums";
import type {
  FlightFormLocation,
  TFlightFormValues,
} from "@/app/lib/types/types";
import { airportSchema, citySchema } from "@/app/lib/utils/locations";

export const flightFormLocationSchema = mixed<FlightFormLocation>()
  .test("is-valid-location", "Invalid location type", (value) => {
    if (!value) return false;

    try {
      if (value.type === "AIRPORT") {
        airportSchema.validateSync(value);
        return true;
      } else if (value.type === "CITY") {
        citySchema.validateSync(value);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  })
  .required();

export const flightFormValuesSchema: ObjectSchema<TFlightFormValues> = object({
  tripType: mixed<TripType>().oneOf(Object.values(TripType)).required(),
  from: flightFormLocationSchema.required().nullable(),
  to: flightFormLocationSchema.required().nullable(),
  dates: object({
    departure: date().required(),
    return: date().required(),
  }).required(),
  adult: number().required().min(1).max(9),
  child: number().required().min(0).max(9),
  infantWithoutSeat: number().required().min(0).max(9),
  cabinClass: mixed<CabinClass>().oneOf(Object.values(CabinClass)).required(),
});

export const getValidationSchema = (t: ReturnType<typeof useTranslations>) =>
  object().shape({
    dates: object().shape({
      departure: date()
        .required(t("flights.validation.dates.departure"))
        .test(
          "departureRequired",
          t("flights.validation.dates.minDeparture"),
          (date) => {
            if (!date) return true;
            return !dayjs(date).endOf("day").isBefore(dayjs(), "day");
          },
        ),
      return: date()
        .optional()
        .min(ref("departure"), t("flights.validation.dates.minReturn"))
        .test(
          "returnRequiredIfRoundTrip",
          t("flights.validation.dates.return"),
          (date, context) => {
            const [, formValues] = context.from!;

            return formValues.value.tripType !== TripType.ROUND_TRIP || !!date;
          },
        ),
    }),
    from: object({
      name: string().required(),
    }).required(t("flights.validation.from")),
    to: object({
      name: string().required(),
    }).required(t("flights.validation.to")),
    tripType: mixed<TripType>()
      .oneOf(Object.values(TripType))
      .required(t("flights.validation.tripType")),
    cabinClass: string().required(t("flights.validation.cabinClass")),
    adult: number().min(1).max(9),
    child: number().min(0).max(9),
    infantWithoutSeat: number().min(0).max(9),
  });
